import React from "react";
import { Link } from "react-router";
import { ChevronsUp } from "lucide-react";

import { getAlignClass, renderColumnData, renderColumnHeader } from "./ItemListColumns";
import type { RenderColumnDataOptions } from "./ItemListColumns";
import SortOrderCell from "./SortOrderCell";
import { getSortedColumnEntries } from "../model/item-list.types";
import type { ItemListColumnDefinition, ItemListColumnPreferences } from "../model/item-list.types";
import type { ItemRowHighlight } from "../model/ItemContext";

interface ItemListRow {
  id: number;
  name?: string;
  sort_order?: number;
  itemgroup?: { id: string; name: string } | null;
  [key: string]: unknown;
}

interface ItemListGroup {
  name: string;
  description: string | null;
  sort_order: number | null;
  items: ItemListRow[];
}

interface ItemListTableProps {
  visibleItems: ItemListRow[];
  groupedItems: Record<string, ItemListGroup>;
  selectedItems: Set<number>;
  highlightedItem: ItemRowHighlight;
  itemListColumnConfig: ItemListColumnDefinition[];
  itemListColumnPreferences: ItemListColumnPreferences;
  draftWidth: { id: string; width: number } | null;
  columnDataOpts: RenderColumnDataOptions;
  filterItemStatus: string;
  filterFloorReached: boolean;
  filterKeepDefaultPrice: string;
  filterCompareRange: string;
  filterAccumulatedError: string;
  isAllSelected: boolean;
  onSelectAll: () => void;
  onSelectGroup: (itemIds: number[]) => void;
  onSelectItem: (itemId: number) => void;
  onOpenGroupSortOrder: (groupId: string, groupData: ItemListGroup) => void;
  onOpenGroupEdit: (event: React.MouseEvent<HTMLAnchorElement>, groupId: string) => void;
  onOpenItemPlacement: (item: ItemListRow) => void;
  onOpenItemSortOrder: (item: ItemListRow) => void;
  onAdjustSortOrder: (target: { type: 'group'; id: string } | { type: 'item'; id: number }, sortOrder: number) => Promise<void>;
  onResizeStart: (event: React.PointerEvent<HTMLSpanElement>, id: string) => void;
  onResizeMove: (event: React.PointerEvent<HTMLSpanElement>) => void;
  onResizeEnd: (event: React.PointerEvent<HTMLSpanElement>) => void;
  onResizeCancel: () => void;
}

export const ItemListTable = ({
  visibleItems, groupedItems, selectedItems, highlightedItem,
  itemListColumnConfig, itemListColumnPreferences, draftWidth, columnDataOpts,
  filterItemStatus, filterFloorReached, filterKeepDefaultPrice, filterCompareRange, filterAccumulatedError, isAllSelected, onSelectAll, onSelectGroup,
  onSelectItem, onOpenGroupSortOrder, onOpenGroupEdit, onOpenItemPlacement, onOpenItemSortOrder, onAdjustSortOrder,
  onResizeStart, onResizeMove, onResizeEnd, onResizeCancel,
}: ItemListTableProps) => {
  const resultNumberByItemId = new Map(
    visibleItems.map((item, index) => [item.id, index + 1]),
  );
  const sortOrderColumnVisible = itemListColumnConfig.some(
    (column) => column.id === 'col-sort-order' && column.visible,
  );

  return (
      <div className="table-scroll item-list-table-scroll">
        <table className="table text-center" style={{ fontSize: '1rem' /*'0.875rem'*/ }}>
          <thead className="table-primary">
            <tr>
              <th scope="col" className="px-1 mx-0 text-center" style={{ minWidth: 38, width: 38 }} title="선택">
                <input
                  type="checkbox"
                  className="form-check-input"
                  checked={isAllSelected}
                  onChange={onSelectAll}
                />
              </th>
              {getSortedColumnEntries(itemListColumnConfig).map(([colId, colConfig]) => (
                renderColumnHeader(colId, colConfig, colConfig.visible && (
                  <span
                    className="item-list-column-resize-handle"
                    title={`${colConfig.label} 너비 조절`}
                    onPointerDown={(event) => onResizeStart(event, colId)}
                    onPointerMove={onResizeMove}
                    onPointerUp={onResizeEnd}
                    onPointerCancel={onResizeCancel}
                  />
                ))
              ))}
            </tr>
          </thead>
          <tbody>
            {Object.entries(groupedItems).map(([groupId, groupData]) => {
              const groupItemIds = groupData.items.map(i => i.id);
              const isGroupSelected = groupItemIds.length > 0 && groupItemIds.every(id => selectedItems.has(id));
              return (
                <React.Fragment key={groupId}>
                  {/* 그룹 헤더 행 */}
                  <tr className="table-group-header table-secondary">
                    <td className="px-0 mx-0">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        checked={isGroupSelected}
                        onChange={() => onSelectGroup(groupItemIds)}
                      />
                    </td>
                    <td colSpan={100} className="text-start">
                      <div className="d-flex align-items-center w-100">
                        {sortOrderColumnVisible && groupId !== 'none' && (
                        <SortOrderCell
                          sortOrder={groupData.sort_order ?? 0}
                          editTitle="그룹 우선순위 수정"
                          onEdit={() => onOpenGroupSortOrder(groupId, groupData)}
                          onAdjust={(sortOrder) => onAdjustSortOrder({ type: 'group', id: groupId }, sortOrder)}
                        />
                        )}
                        {/* 1. 그룹명 - 줄바꿈 절대 금지 */}
                        <div className={`flex-shrink-0 fw-bold text-nowrap ${sortOrderColumnVisible && groupId !== 'none' ? 'ms-2' : ''}`}>
                          {(groupId === 'none')
                            ? groupData.name
                            : <Link to={`/itemgroups/${groupId}`} onClick={(event) => onOpenGroupEdit(event, groupId)}>{groupData.name}</Link>
                          }
                        </div>

                        {/* 3. 그룹 메모 & 그룹 아이템 수 (남은 공간 다 차지하고 양끝 정렬) */}
                        <div className="d-flex flex-grow-1 justify-content-between align-items-center min-w-0">
                          <div className="small text-muted ms-2 text-truncate">
                            {groupData.description || ""}
                          </div>
                          <div className="small flex-shrink-0 ms-4">({groupData.items.length})</div>
                        </div>

                      </div>
                    </td>
                  </tr>
                  {/* 그룹 아이템 행 */}
                  {groupData.items.map(item => {
                    const rowHighlightClass =
                      highlightedItem?.itemId === String(item.id)
                        ? highlightedItem.type === "focus"
                          ? "item-focus-highlight"
                          : "item-process-highlight"
                        : undefined;
                    return (
                    <tr
                      key={item.id}
                      data-item-id={item.id}
                      className={rowHighlightClass}
                    >
                      <td className="px-0 mx-0">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          checked={selectedItems.has(item.id)}
                          onChange={() => onSelectItem(item.id)}
                        />
                      </td>
                      {getSortedColumnEntries(itemListColumnConfig).map(([colId, colConfig]) => (
                        <td
                          key={colId}
                          style={
                            colConfig.maxWidth !== null
                              ? {
                                  minWidth: colConfig.minWidth,
                                  width: colConfig.maxWidth,
                                  maxWidth: colConfig.maxWidth,
                                  ...(draftWidth?.id === colId || itemListColumnPreferences.widths?.[colId] !== undefined
                                    ? { overflow: 'hidden', textOverflow: 'ellipsis' }
                                    : {}),
                                }
                              : { minWidth: colConfig.minWidth }
                          }
                          className={`${colConfig.visible ? 'mx-0 px-0' : 'd-none'} ${getAlignClass(colConfig.align)} ${colId === 'col-processed-at' ? 'item-list-scroll-edge' : ''}`.trim()}
                        >
                          {colId === 'col-result-number' ? (
                            colConfig.visible && (
                              <button
                                type="button"
                                className="btn p-0 border-0 text-muted d-inline-flex align-items-center gap-1"
                                style={{ fontSize: "0.75rem", lineHeight: 1 }}
                                data-result-number={resultNumberByItemId.get(item.id)}
                                title="상품그룹 및 그룹 내 순서 변경"
                                aria-label={`${resultNumberByItemId.get(item.id)}번 상품의 그룹 및 순서 변경`}
                                onClick={() => onOpenItemPlacement(item)}
                              >
                                {resultNumberByItemId.get(item.id)}
                                {(item.is_urgent_enqueue_pending === true || item.is_urgent_enqueue_processing === true) && (
                                  <ChevronsUp
                                    size={14}
                                    style={{ color: "var(--accent)" }}
                                    aria-label={item.is_urgent_enqueue_processing === true ? "우선 처리중" : "우선 실행 대기"}
                                  />
                                )}
                              </button>
                            )
                          ) : colId === 'col-sort-order' ? (
                            colConfig.visible && (
                              <SortOrderCell
                                sortOrder={item.sort_order ?? 0}
                                editTitle="상품 우선순위 수정"
                                onEdit={() => onOpenItemSortOrder(item)}
                                onAdjust={(sortOrder) => onAdjustSortOrder({ type: 'item', id: item.id }, sortOrder)}
                              />
                            )
                          ) : renderColumnData(colId, item, columnDataOpts)}
                        </td>
                      ))}
                    </tr>
                    );
                  })}
                </React.Fragment>
              )
            })}
            {visibleItems.length < 1
              && <tr>
                <td colSpan={100} style={{ height: 100, textAlign: 'center', verticalAlign: 'middle' }}>
                  {filterItemStatus || filterFloorReached || filterKeepDefaultPrice || filterCompareRange || filterAccumulatedError ? '조건에 맞는 상품이 없습니다.' : '등록된 상품이 없습니다.'}
                </td>
              </tr>
            }
          </tbody>
        </table>
      </div>
  );
};
